// import { StrictMode } from 'react';
import { GlobalStyle } from '@chakra-ui/react';
import { EnvironmentValues } from '@llx/common';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { RecoilRoot } from 'recoil';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - - On the pipeline, npm run type:check, this file does not exist
import App from './App';
import './assets/css/App.css';
// css imports
// TODO: remove this and use styled components
import './assets/css/fonts.css';
import './assets/css/index.css';
import { KeycloakProvider } from './auth/KeycloakProvider';
import { UserAuthenticationContextProvider } from './components/AuthTokenContextProvider/UserAuthContext';
import { CustomApolloProvider } from './components/CustomApolloProvider/CustomApolloProvider';
import './config/i18n';
import { config } from './environment';
import { initGA } from './features/analytic/analytics.js';

// --------------------- sentry ------------------------
if (config.ENVIRONMENT !== EnvironmentValues.LOCAL) {
  Sentry.init({
    dsn: 'https://8bb3fd42305d142560888fab04b4962f@o4509587308478464.ingest.de.sentry.io/4509756896575568',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
    environment: config.ENVIRONMENT,

    release: config.VERSION,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.5,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: config.ENVIRONMENT === EnvironmentValues.SIMULATION ? 0.01 : 0.2,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: config.ENVIRONMENT === EnvironmentValues.SIMULATION ? 0.01 : 1.0,

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}

if (config.ENVIRONMENT === EnvironmentValues.PROD) {
  initGA();
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 90000,
      retry: 3,
      retryDelay(failureCount, error) {
        // Exponential backoff
        return Math.min(1000 * 2 ** failureCount, 10000);
      },
      enabled: true,
    },
    mutations: {
      retry: 3,
      retryDelay(failureCount, error) {
        // Exponential backoff
        return Math.min(1000 * 2 ** failureCount, 10000);
      },
    },
  },
});

// --------------------- render ------------------------

createRoot(document.getElementById('root') as HTMLElement).render(
  // TODO: strictmode must be disabled to avoid warnings from Antd Animation.
  // We must wait for antd to upgrade and remove the use of "findByNode", or we can migrate all antd components to another library.
  // <StrictMode>
  <RecoilRoot>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <KeycloakProvider>
          <CustomApolloProvider>
            <UserAuthenticationContextProvider>
              <GlobalStyle />
              <App />
            </UserAuthenticationContextProvider>
          </CustomApolloProvider>
        </KeycloakProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </RecoilRoot>
  // </StrictMode>
);
